import React, { useEffect, useMemo, useRef, useState } from 'react'
import {
  Alert,
  Button,
  Checkbox,
  Icon,
  Modal,
  Progress,
  Tag,
  message
} from 'antd'
import './BulkClipDownloadModal.scss'

const padNumber = value => String(value).padStart(2, '0')

const sanitizeFilenamePart = value =>
  String(value || '')
    .trim()
    .replace(/[^a-zA-Z0-9-_]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .toLowerCase()

const getFileExtension = url => {
  const cleanUrl = String(url || '').split('?')[0].split('#')[0]
  const match = cleanUrl.match(/\.([a-zA-Z0-9]{2,5})$/)
  return match ? match[1].toLowerCase() : 'mp4'
}

const getSceneLabel = (scene, sceneIndex) =>
  scene?.title?.trim() || `Scene ${sceneIndex + 1}`

const formatBytes = bytes => {
  if (!bytes) return '0 MB'
  const mb = bytes / (1024 * 1024)
  return `${mb.toFixed(2)} MB`
}

const buildClipRows = scenes =>
  (scenes || []).flatMap((scene, sceneIndex) =>
    (scene.shots || []).map((shot, shotIndex) => {
      const sceneNumber = sceneIndex + 1
      const shotNumber = shotIndex + 1
      const sceneId = scene.id || `scene-${sceneIndex}`
      const shotId = shot.id || `shot-${shotIndex}`
      const sceneSlug =
        sanitizeFilenamePart(scene?.title) || `scene-${padNumber(sceneNumber)}`
      const extension = getFileExtension(shot?.vidSrc)

      return {
        id: `${sceneId}-${shotId}`,
        sceneId,
        shotId,
        sceneIndex,
        shotIndex,
        sceneLabel: getSceneLabel(scene, sceneIndex),
        shotLabel: `Shot ${shotNumber}`,
        url: shot?.vidSrc || '',
        isStatic: !!shot?.is_static,
        filename: `scene-${padNumber(sceneNumber)}-shot-${padNumber(
          shotNumber
        )}-${sceneSlug}-video.${extension}`
      }
    })
  )

const triggerBlobDownload = (blob, filename) => {
  const url = window.URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  link.style.display = 'none'
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  window.setTimeout(() => window.URL.revokeObjectURL(url), 2000)
}

const fetchClipBlob = async (clip, abortController, onProgress) => {
  const response = await fetch(clip.url, {
    method: 'GET',
    headers: {
      Accept: 'video/mp4,video/webm,video/*,*/*'
    },
    signal: abortController.signal
  })

  if (!response.ok) {
    throw new Error(`Download failed with status ${response.status}`)
  }

  const totalBytes = Number(response.headers.get('content-length')) || 0

  if (!response.body || !response.body.getReader) {
    const blob = await response.blob()
    onProgress({
      receivedBytes: blob.size || totalBytes,
      totalBytes: blob.size || totalBytes
    })
    return blob
  }

  const reader = response.body.getReader()
  const chunks = []
  let receivedBytes = 0

  while (true) {
    const { done, value } = await reader.read()
    if (done) break

    chunks.push(value)
    receivedBytes += value.length
    onProgress({ receivedBytes, totalBytes })
  }

  return new Blob(chunks, {
    type: response.headers.get('content-type') || 'video/mp4'
  })
}

const BulkClipDownloadModal = ({ visible, scenes = [], videoId, onClose }) => {
  const [selectedClipIds, setSelectedClipIds] = useState([])
  const [isDownloading, setIsDownloading] = useState(false)
  const [downloadedClipIds, setDownloadedClipIds] = useState([])
  const [failedClipIds, setFailedClipIds] = useState([])
  const [currentClipId, setCurrentClipId] = useState(null)
  const [currentProgress, setCurrentProgress] = useState({
    receivedBytes: 0,
    totalBytes: 0
  })
  const [isComplete, setIsComplete] = useState(false)
  const abortRef = useRef(null)

  const allClipRows = useMemo(() => buildClipRows(scenes), [scenes])
  const downloadableRows = useMemo(
    () => allClipRows.filter(row => row.url),
    [allClipRows]
  )
  const skippedRows = useMemo(
    () => allClipRows.filter(row => !row.url),
    [allClipRows]
  )
  const selectedClipIdSet = useMemo(
    () => new Set(selectedClipIds),
    [selectedClipIds]
  )
  const selectedRows = useMemo(
    () => downloadableRows.filter(row => selectedClipIdSet.has(row.id)),
    [downloadableRows, selectedClipIdSet]
  )
  const groupedScenes = useMemo(
    () =>
      (scenes || []).map((scene, index) => {
        const sceneId = scene.id || `scene-${index}`
        const rows = allClipRows.filter(row => row.sceneId === sceneId)
        const readyRows = rows.filter(row => row.url)
        const selectedReadyRows = readyRows.filter(row =>
          selectedClipIdSet.has(row.id)
        )

        return {
          id: sceneId,
          label: getSceneLabel(scene, index),
          rows,
          readyRows,
          selectedReadyRows,
          checked:
            readyRows.length > 0 && selectedReadyRows.length === readyRows.length,
          indeterminate:
            selectedReadyRows.length > 0 &&
            selectedReadyRows.length < readyRows.length
        }
      }),
    [allClipRows, scenes, selectedClipIdSet]
  )

  const currentClip = selectedRows.find(row => row.id === currentClipId)
  const processedCount = downloadedClipIds.length + failedClipIds.length
  const batchPercent = selectedRows.length
    ? Math.round((processedCount / selectedRows.length) * 100)
    : 0
  const currentPercent = currentProgress.totalBytes
    ? Math.min(
        100,
        Math.round(
          (currentProgress.receivedBytes / currentProgress.totalBytes) * 100
        )
      )
    : undefined
  const displayedPercent = isDownloading ? currentPercent || 0 : batchPercent

  useEffect(() => {
    if (!visible) return

    setSelectedClipIds(downloadableRows.map(row => row.id))
    setDownloadedClipIds([])
    setFailedClipIds([])
    setCurrentClipId(null)
    setCurrentProgress({ receivedBytes: 0, totalBytes: 0 })
    setIsDownloading(false)
    setIsComplete(false)
    abortRef.current = null
  }, [downloadableRows, visible])

  const resetProgress = () => {
    setDownloadedClipIds([])
    setFailedClipIds([])
    setCurrentClipId(null)
    setCurrentProgress({ receivedBytes: 0, totalBytes: 0 })
    setIsComplete(false)
  }

  const handleToggleClip = (clipId, checked) => {
    resetProgress()
    setSelectedClipIds(prev => {
      if (checked) return [...new Set([...prev, clipId])]
      return prev.filter(id => id !== clipId)
    })
  }

  const handleToggleScene = (sceneGroup, checked) => {
    resetProgress()
    const sceneClipIds = sceneGroup.readyRows.map(row => row.id)
    setSelectedClipIds(prev => {
      if (checked) {
        return [...new Set([...prev, ...sceneClipIds])]
      }

      return prev.filter(id => !sceneClipIds.includes(id))
    })
  }

  const handleSelectAll = () => {
    resetProgress()
    setSelectedClipIds(downloadableRows.map(row => row.id))
  }

  const handleClearSelection = () => {
    resetProgress()
    setSelectedClipIds([])
  }

  const handleStopDownloads = () => {
    if (abortRef.current) {
      abortRef.current.abort()
      abortRef.current = null
    }

    setIsDownloading(false)
    setCurrentClipId(null)
    message.info('Stopped the current download batch.')
  }

  const handleClose = () => {
    if (isDownloading) {
      handleStopDownloads()
    }
    onClose()
  }

  const handleStartDownloads = async () => {
    if (!selectedRows.length) {
      message.warning('Select at least one generated clip to download.')
      return
    }

    resetProgress()
    setIsDownloading(true)
    let wasStopped = false
    let failedCount = 0

    for (const clip of selectedRows) {
      const abortController = new AbortController()
      abortRef.current = abortController
      setCurrentClipId(clip.id)
      setCurrentProgress({ receivedBytes: 0, totalBytes: 0 })

      try {
        const blob = await fetchClipBlob(clip, abortController, progress => {
          setCurrentProgress(progress)
        })

        if (abortController.signal.aborted) {
          break
        }

        triggerBlobDownload(blob, clip.filename)
        setDownloadedClipIds(prev => [...prev, clip.id])
      } catch (error) {
        if (abortController.signal.aborted) {
          wasStopped = true
          break
        }

        console.error('Clip download failed:', error)
        failedCount += 1
        setFailedClipIds(prev => [...prev, clip.id])
      } finally {
        abortRef.current = null
      }
    }

    setCurrentClipId(null)
    setCurrentProgress({ receivedBytes: 0, totalBytes: 0 })
    setIsDownloading(false)

    if (wasStopped) {
      message.info('Download batch stopped.')
      return
    }

    setIsComplete(true)
    if (failedCount) {
      message.warning('Finished with some failed clips.')
    } else {
      message.success('Finished processing selected clips.')
    }
  }

  const modalTitle = (
    <div className="bulk-clip-download__title">
      <Icon type="download" />
      <span>Download Clips</span>
    </div>
  )

  return (
    <Modal
      visible={visible}
      title={modalTitle}
      onCancel={handleClose}
      footer={null}
      width={760}
      centered
      destroyOnClose
      className="bulk-clip-download"
      maskClosable={!isDownloading}
    >
      <div className="bulk-clip-download__layout">
        <Alert
          type="info"
          showIcon
          message="No extra tabs"
          description="Selected clips are downloaded one by one and saved by the browser without opening video tabs. Keep this window open until the batch finishes."
        />

        <div className="bulk-clip-download__summary">
          <div>
            <span className="bulk-clip-download__metric">
              {selectedRows.length}
            </span>
            <span className="bulk-clip-download__label">selected</span>
          </div>
          <div>
            <span className="bulk-clip-download__metric">
              {downloadableRows.length}
            </span>
            <span className="bulk-clip-download__label">ready clips</span>
          </div>
          <div>
            <span className="bulk-clip-download__metric">
              {skippedRows.length}
            </span>
            <span className="bulk-clip-download__label">skipped</span>
          </div>
        </div>

        <div className="bulk-clip-download__toolbar">
          <div className="bulk-clip-download__section-title">
            Choose scenes and shots
          </div>
          <div className="bulk-clip-download__toolbar-actions">
            <Button size="small" onClick={handleSelectAll}>
              Select all
            </Button>
            <Button size="small" onClick={handleClearSelection}>
              Clear
            </Button>
          </div>
        </div>

        <div className="bulk-clip-download__progress-panel">
          <div className="bulk-clip-download__progress-head">
            <div>
              <strong>
                {isDownloading
                  ? 'Downloading current clip'
                  : isComplete
                  ? 'Batch finished'
                  : 'Ready to download'}
              </strong>
              <span>
                {currentClip
                  ? currentClip.filename
                  : `${downloadedClipIds.length} downloaded, ${failedClipIds.length} failed`}
              </span>
            </div>
            {isDownloading && <Icon type="loading" />}
            {isComplete && <Icon type="check-circle" />}
          </div>
          <Progress
            percent={displayedPercent}
            status={
              failedClipIds.length
                ? 'exception'
                : isComplete
                ? 'success'
                : 'active'
            }
          />
          <div className="bulk-clip-download__progress-meta">
            <span>Batch: {batchPercent}%</span>
            <span>
              Current:{' '}
              {formatBytes(currentProgress.receivedBytes)}
              {currentProgress.totalBytes > 0 &&
                ` / ${formatBytes(currentProgress.totalBytes)}`}
            </span>
          </div>
        </div>

        <div className="bulk-clip-download__list">
          {groupedScenes.map(sceneGroup => (
            <div className="bulk-clip-download__scene-block" key={sceneGroup.id}>
              <div className="bulk-clip-download__scene-head">
                <Checkbox
                  checked={sceneGroup.checked}
                  indeterminate={sceneGroup.indeterminate}
                  disabled={!sceneGroup.readyRows.length || isDownloading}
                  onChange={event =>
                    handleToggleScene(sceneGroup, event.target.checked)
                  }
                >
                  <span className="bulk-clip-download__scene-name">
                    {sceneGroup.label}
                  </span>
                </Checkbox>
                <span className="bulk-clip-download__scene-count">
                  {sceneGroup.selectedReadyRows.length} of{' '}
                  {sceneGroup.readyRows.length} selected
                </span>
              </div>

              {sceneGroup.rows.map(row => {
                const isSelected = selectedClipIdSet.has(row.id)
                const isDownloaded = downloadedClipIds.includes(row.id)
                const hasFailed = failedClipIds.includes(row.id)
                const isCurrent = currentClipId === row.id

                return (
                  <div
                    className={`bulk-clip-download__row ${
                      isCurrent ? 'bulk-clip-download__row--current' : ''
                    }`}
                    key={row.id}
                  >
                    <div className="bulk-clip-download__row-main">
                      <Checkbox
                        checked={isSelected}
                        disabled={!row.url || isDownloading}
                        onChange={event =>
                          handleToggleClip(row.id, event.target.checked)
                        }
                      />
                      <strong>{row.shotLabel}</strong>
                      {row.url ? (
                        <small title={row.filename}>{row.filename}</small>
                      ) : (
                        <small>
                          {row.isStatic
                            ? 'Static shot has no generated video clip'
                            : 'No generated video clip yet'}
                        </small>
                      )}
                    </div>
                    {!row.url ? (
                      <Tag color={row.isStatic ? 'blue' : 'orange'}>
                        {row.isStatic ? 'Static' : 'Missing'}
                      </Tag>
                    ) : isCurrent ? (
                      <Tag color="processing">Downloading</Tag>
                    ) : hasFailed ? (
                      <Tag color="red">Failed</Tag>
                    ) : isDownloaded ? (
                      <Tag color="green">Downloaded</Tag>
                    ) : isSelected ? (
                      <Tag color="geekblue">Selected</Tag>
                    ) : (
                      <Tag>Ready</Tag>
                    )}
                  </div>
                )
              })}
            </div>
          ))}
        </div>

        <div className="bulk-clip-download__actions">
          <Button onClick={handleClose}>{isComplete ? 'Close' : 'Cancel'}</Button>
          {isDownloading ? (
            <Button onClick={handleStopDownloads}>Stop</Button>
          ) : (
            <Button
              type="primary"
              icon="download"
              onClick={handleStartDownloads}
              disabled={!selectedRows.length}
            >
              Download Selected Clips
            </Button>
          )}
        </div>

        {videoId && (
          <div className="bulk-clip-download__footnote">
            Download set: {videoId}
          </div>
        )}
      </div>
    </Modal>
  )
}

export default BulkClipDownloadModal
